import type { GuideQuestion, GuideQuestionCategoryDef } from "../types/guide-content.types.ts"
import { isGuideQuestionAccessible } from "./guide-permissions.ts"
import { GUIDE_QUESTION_CATEGORIES } from "./question-categories.ts"
import { GUIDE_QUESTIONS } from "./questions.ts"

/** Every question this user's permissions grant — the one list every Q&A
 * surface (browse, category filter, search, future contextual panels)
 * should read from, never the raw `GUIDE_QUESTIONS` array directly. */
export const getAccessibleQuestions = (
  pages: readonly string[] | null | undefined,
  codes: unknown
): GuideQuestion[] =>
  GUIDE_QUESTIONS.filter((question) => isGuideQuestionAccessible(question, pages, codes))

export interface GuideQuestionCategoryGroup {
  category: GuideQuestionCategoryDef
  questions: GuideQuestion[]
}

/** Accessible categories, in declared order, each with its own accessible
 * questions — a category with zero accessible questions is dropped
 * entirely, the same "permission AND real content" rule Browse by Area
 * uses for a Learning Area. */
export const getAccessibleQuestionCategories = (
  pages: readonly string[] | null | undefined,
  codes: unknown
): GuideQuestionCategoryGroup[] => {
  const accessible = getAccessibleQuestions(pages, codes)
  return GUIDE_QUESTION_CATEGORIES.map((category) => ({
    category,
    questions: accessible.filter((question) => question.category === category.id)
  })).filter((group) => group.questions.length > 0)
}

export const getQuestion = (
  id: string,
  pages: readonly string[] | null | undefined,
  codes: unknown
): GuideQuestion | null =>
  getAccessibleQuestions(pages, codes).find((question) => question.id === id) ?? null
